import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'react-hot-toast';
import { 
  ClipboardList, ArrowLeft, Save, Loader2, Package, Truck, 
  Calendar, AlertCircle, CheckCircle2, Check 
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PickListCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedDelivery = searchParams.get('delivery');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDeliveries, setSelectedDeliveries] = useState({});
  const [shippingPoints, setShippingPoints] = useState([]);

  const [formData, setFormData] = useState({
    shipping_point: '',
    planned_gi_date: new Date().toISOString().split('T')[0],
    group_by_storage_location: true,
    optimize_pick_path: true
  });


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load deliveries that are eligible for picking (CREATED status, not yet picked)
      const [delRes, spRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/otc/deliveries?status=CREATED`),
        axios.get(`${BACKEND_URL}/api/sales-distribution/shipping-points`).catch(() => ({ data: [] }))
      ]);

      const eligibleDeliveries = (delRes.data || []).filter(d => 
        d.status === 'CREATED' && d.picking_status !== 'PICKED'
      );
      setDeliveries(eligibleDeliveries);

      // Use shipping points from API
      const shippingPointsData = (spRes.data || []).filter(sp => sp.status === 'Active');
      setShippingPoints(shippingPointsData);

      // Pre-select delivery if passed in URL
      if (preSelectedDelivery) {
        const delivery = eligibleDeliveries.find(d => d.delivery_number === preSelectedDelivery);
        if (delivery) {
          setSelectedDeliveries({ [preSelectedDelivery]: true });
          if (delivery.shipping_point) {
            setFormData(prev => ({ ...prev, shipping_point: delivery.shipping_point }));
          }
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load deliveries');
    } finally {
      setIsLoading(false);
    }
  }, [preSelectedDelivery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeliveryToggle = (deliveryNumber) => {
    setSelectedDeliveries(prev => ({
      ...prev,
      [deliveryNumber]: !prev[deliveryNumber]
    }));
  };

  const getFilteredDeliveries = () => {
    if (!formData.shipping_point) return deliveries;
    return deliveries.filter(d => d.shipping_point === formData.shipping_point);
  };

  const filteredDeliveries = getFilteredDeliveries();

  const isAllSelected = filteredDeliveries.length > 0 && 
    filteredDeliveries.every(d => selectedDeliveries[d.delivery_number]);

  const handleSelectAll = () => {
    const newSelected = { ...selectedDeliveries };
    if (isAllSelected) {
      // Deselect all filtered
      filteredDeliveries.forEach(d => delete newSelected[d.delivery_number]);
    } else {
      // Select all filtered
      filteredDeliveries.forEach(d => newSelected[d.delivery_number] = true);
    }
    setSelectedDeliveries(newSelected);
  };

  const getSelectedCount = () => {
    return Object.values(selectedDeliveries).filter(Boolean).length;
  };

  const getTotalItems = () => {
    return deliveries
      .filter(d => selectedDeliveries[d.delivery_number])
      .reduce((sum, d) => sum + (d.items?.length || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedDeliveryNumbers = Object.entries(selectedDeliveries)
      .filter(([_, selected]) => selected)
      .map(([num, _]) => num);

    if (selectedDeliveryNumbers.length === 0) {
      toast.error('Please select at least one delivery');
      return;
    }

    if (!formData.shipping_point) {
      toast.error('Please select a shipping point');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        shipping_point: formData.shipping_point,
        planned_gi_date: formData.planned_gi_date,
        delivery_numbers: selectedDeliveryNumbers,
        group_by_storage_location: formData.group_by_storage_location,
        optimize_pick_path: formData.optimize_pick_path,
        created_by: 'ADMIN'
      };

      const response = await axios.post(`${BACKEND_URL}/api/warehouse/pick-lists`, payload);
      
      toast.success(`Pick List ${response.data.pick_list_number} created successfully!`);
      navigate(`/warehouse/pick-lists/${response.data.pick_list_number}`);
    } catch (error) {
      console.error('Error creating pick list:', error);
      toast.error(error.response?.data?.detail || 'Failed to create pick list');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="pick-list-create-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/warehouse/pick-lists')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-amber-600" />
              Create Pick List
            </h1>
            <p className="text-sm text-gray-500">Select deliveries for wave picking</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Settings */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Pick List Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Shipping Point *</Label>
                      <select
                        value={formData.shipping_point}
                        onChange={(e) => setFormData({ ...formData, shipping_point: e.target.value })}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                        required
                      >
                        <option value="">Select Shipping Point</option>
                        {shippingPoints.map(sp => (
                          <option key={sp.shipping_point_id || sp} value={sp.shipping_point_id || sp}>
                            {sp.shipping_point_id || sp} {sp.name ? `- ${sp.name}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Planned Goods Issue Date</Label>
                      <Input
                        type="date"
                        value={formData.planned_gi_date}
                        onChange={(e) => setFormData({ ...formData, planned_gi_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="space-y-3 pt-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="group_by_storage"
                          checked={formData.group_by_storage_location}
                          onChange={(e) => setFormData({ ...formData, group_by_storage_location: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <Label htmlFor="group_by_storage" className="text-sm cursor-pointer">
                          Group by Storage Location
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="optimize_path"
                          checked={formData.optimize_pick_path}
                          onChange={(e) => setFormData({ ...formData, optimize_pick_path: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <Label htmlFor="optimize_path" className="text-sm cursor-pointer">
                          Optimize Pick Path
                        </Label>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Summary */}
                <Card className="p-6 bg-amber-50 border-amber-200">
                  <h3 className="text-lg font-semibold mb-4 text-amber-800">Selection Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deliveries Selected:</span>
                      <span className="font-semibold text-amber-800">{getSelectedCount()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Items:</span>
                      <span className="font-semibold text-amber-800">{getTotalItems()}</span>
                    </div>
                  </div>
                </Card>

                <Button 
                  type="submit" 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  disabled={isSaving || getSelectedCount() === 0}
                >
                  {isSaving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Create Pick List</>
                  )}
                </Button>
              </div>

              {/* Right Column - Deliveries */}
              <div className="lg:col-span-2">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Available Deliveries</h3>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {isAllSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  {filteredDeliveries.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500">No deliveries available for picking</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {formData.shipping_point 
                          ? 'Try selecting a different shipping point'
                          : 'Select a shipping point to filter deliveries'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {filteredDeliveries.map(delivery => (
                        <div
                          key={delivery.delivery_number}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            selectedDeliveries[delivery.delivery_number]
                              ? 'border-amber-500 bg-amber-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleDeliveryToggle(delivery.delivery_number)}
                        >
                          <div className="flex items-start gap-3">
                            <div 
                              className={`h-4 w-4 shrink-0 rounded-sm border shadow flex items-center justify-center cursor-pointer ${
                                selectedDeliveries[delivery.delivery_number] 
                                  ? 'bg-amber-600 border-amber-600 text-white' 
                                  : 'border-gray-300 bg-white'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeliveryToggle(delivery.delivery_number);
                              }}
                            >
                              {selectedDeliveries[delivery.delivery_number] && (
                                <Check className="h-3 w-3" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-gray-900">
                                  {delivery.delivery_number}
                                </span>
                                <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded">
                                  {delivery.status}
                                </span>
                              </div>
                              <div className="mt-1 text-sm text-gray-600">
                                <div className="flex items-center gap-4">
                                  <span>SO: {delivery.sales_order_number}</span>
                                  <span>Items: {delivery.items?.length || 0}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <Truck className="w-3 h-3" />
                                  <span>{delivery.ship_to_party_name || delivery.ship_to_party}</span>
                                </div>
                                {delivery.planned_goods_issue_date && (
                                  <div className="flex items-center gap-2 mt-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>GI Date: {delivery.planned_goods_issue_date}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
};

export default PickListCreate;
